import { WaveformCanvas } from './WaveformCanvas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Group, Text, ActionIcon, Menu, Tooltip } from '@mantine/core';
import Play from 'lucide-react/dist/esm/icons/play';
import Pause from 'lucide-react/dist/esm/icons/pause';
import Scissors from 'lucide-react/dist/esm/icons/scissors';
import Trash from 'lucide-react/dist/esm/icons/trash-2';
import type { DisplayBlock, Segment } from '../types';
import { formatTime, parseTime, TL_INTRO, TL_OTHER, TL_SELECTED } from '../utils';
import classes from '../editor.module.css';


/** Zooming in stops once this much of the timeline is still visible. */
const MIN_VISIBLE_SECONDS = 5;

interface Props {
  displayBlocks: DisplayBlock[]; waveforms: Record<string, number[]>; defaultIntro?: string; globalVolume: number;
  segments: Segment[]; currentTime: number; totalDuration: number; zoomLevel: number; viewStart: number;
  isPlaying: boolean; selectedSegId: string | null; onTogglePlay: () => void; onSeek: (time: number, reason: string) => void;
  onTimeChange: (time: number) => void; onZoomChange: (zoom: number, viewStart: number) => void;
  onSelectSegment: (id: string | null) => void; onSplit: (segId: string, time: number) => void; onSplitAtCursor?: () => void;
  onRemove: (segId: string) => void; onToggleFade: (segId: string, field: 'fade_in' | 'fade_out') => void;
  onReorder: (fromSegId: string, toSegId: string) => void; onAppendFromBin: (path: string) => void;
}

export function Timeline({
  displayBlocks, waveforms, defaultIntro, segments, currentTime, totalDuration, zoomLevel, viewStart,
  isPlaying, selectedSegId, onTogglePlay, onSeek, onTimeChange, onZoomChange, onSelectSegment,
  onSplit, onSplitAtCursor, onRemove, onToggleFade, onReorder, onAppendFromBin,
}: Props) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuCtx, setMenuCtx] = useState<{ segId: string; time: number } | null>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [dragOverSegId, setDragOverSegId] = useState<string | null>(null);
  const [editingTime, setEditingTime] = useState(false);
  const [draftTime, setDraftTime] = useState('');
  const timeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editingTime) { timeInputRef.current?.focus(); timeInputRef.current?.select(); } }, [editingTime]);

  const commitTime = () => {
    setEditingTime(false);
    const trimmed = draftTime.trim();
    if (trimmed) {
      const sec = parseTime(trimmed);
      if (!isNaN(sec)) onSeek(Math.max(0, Math.min(totalDuration > 0 ? totalDuration : 999999, sec)), 'ManualTimeInput');
    }
  };

  const timeFromClientX = useCallback((clientX: number): number => {
    const el = timelineRef.current;
    if (!el || totalDuration <= 0) return 0;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return viewStart + (x / rect.width) * (totalDuration / zoomLevel);
  }, [totalDuration, zoomLevel, viewStart]);

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;

    function onMouseDown(e: MouseEvent) { if (e.button === 0) isDragging.current = true; }
    function onMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      const t = timeFromClientX(e.clientX);
      onTimeChange(t);
      if (scrubTimer.current) clearTimeout(scrubTimer.current);
      scrubTimer.current = setTimeout(() => onSeek(t, 'Timeline'), 250);
    }
    function onMouseUp(e: MouseEvent) {
      if (!isDragging.current) return;
      isDragging.current = false;
      if (scrubTimer.current) clearTimeout(scrubTimer.current);
      onSeek(timeFromClientX(e.clientX), 'Timeline');
    }
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      if (totalDuration <= 0) return;
      const curDur = totalDuration / zoomLevel;
      if (e.deltaY !== 0) {
        const rect = el!.getBoundingClientRect();
        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        const newZoom = Math.max(1, Math.min(totalDuration / MIN_VISIBLE_SECONDS, zoomLevel * factor));
        const newDur = totalDuration / newZoom;
        const mouseTime = viewStart + ((e.clientX - rect.left) / rect.width) * curDur;
        onZoomChange(newZoom, Math.max(0, Math.min(totalDuration - newDur, mouseTime - ((e.clientX - rect.left) / rect.width) * newDur)));
      } else if (e.deltaX !== 0) {
        onZoomChange(zoomLevel, Math.max(0, Math.min(totalDuration - curDur, viewStart + (e.deltaX / 300) * curDur)));
      }
    }

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [totalDuration, zoomLevel, viewStart, onSeek, onTimeChange, onZoomChange, timeFromClientX]);

  const viewDuration = totalDuration > 0 ? totalDuration / zoomLevel : 0;
  const cursorPercent = viewDuration > 0 ? ((currentTime - viewStart) / viewDuration) * 100 : 0;

  return (
    <Box p="sm" className={classes.timelineWrapper}>
      <Group gap="sm" mb="xs">
        <Tooltip label={isPlaying ? "Пауза (пробел)" : "Воспроизведение (пробел)"} withArrow openDelay={300}>
          <ActionIcon size="lg" radius="md" variant="light" onClick={onTogglePlay} aria-label={isPlaying ? "Пауза" : "Воспроизведение"}>
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Разрезать по курсору воспроизведения (S)" withArrow>
          <ActionIcon size="lg" radius="md" variant="light" color="blue" onClick={onSplitAtCursor} aria-label="Разрезать по курсору">
            <Scissors size={18} />
          </ActionIcon>
        </Tooltip>
        <Group gap={4} align="center">
          {editingTime ? (
            <input ref={timeInputRef} className={classes.timeInput} style={{ width: 110, fontWeight: 500, textAlign: 'center' }} value={draftTime} onChange={e => setDraftTime(e.currentTarget.value)} onBlur={commitTime} onKeyDown={e => { if (e.key === 'Enter') commitTime(); else if (e.key === 'Escape') setEditingTime(false); }} />
          ) : (
            <Tooltip label="Нажмите, чтобы ввести время вручную" withArrow openDelay={300}>
              <span className={classes.timeValue} style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => { setDraftTime(formatTime(currentTime)); setEditingTime(true); }}>
                {formatTime(currentTime)}
              </span>
            </Tooltip>
          )}
          <Text c="dimmed">/</Text><Text>{formatTime(totalDuration)}</Text>
        </Group>
        {zoomLevel > 1.05 && <Text c="brand" ml="auto">{zoomLevel.toFixed(1)}×</Text>}
      </Group>

      <div className={classes.timeline} ref={timelineRef} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const p = e.dataTransfer.getData('text/plain'); if (p && !e.dataTransfer.getData('application/x-seg-id')) onAppendFromBin(p); }}>
        {/* Grabbing the line itself must scrub: without preventDefault the press would start
            dragging the segment block under it (blocks are draggable for reordering) or select text. */}
        <div className={classes.timelineCursor} style={{ left: `${Math.max(0, Math.min(100, cursorPercent))}%` }}
          onMouseDown={e => { if (e.button !== 0) return; e.preventDefault(); e.stopPropagation(); isDragging.current = true; }} />
        <div className={classes.timelineTracks} style={{ width: `${zoomLevel * 100}%`, transform: totalDuration > 0 ? `translateX(-${(viewStart / totalDuration) * 100}%)` : undefined }}>
          {displayBlocks.map(b => {
            const isIntro = b.segId === null, isSelected = !isIntro && b.segId === selectedSegId;
            const visStart = Math.max(b.globalStart, viewStart), visEnd = Math.min(b.globalStart + b.duration, viewStart + viewDuration);
            const isVisible = viewDuration > 0 && visEnd > visStart;
            const offsetSec = isVisible ? Math.max(0, visStart - b.globalStart) : 0, visDuration = isVisible ? Math.max(0.1, visEnd - visStart) : b.duration;
            const leftPercent = (b.duration > 0 && isVisible) ? (offsetSec / b.duration) * 100 : 0, widthPercent = (b.duration > 0 && isVisible) ? (visDuration / b.duration) * 100 : 100;

            const seg = segments.find(s => s.id === b.segId);
            const mainPath = isIntro ? defaultIntro : (seg?.audio || seg?.video);
            const resolvedStart = (b.sourceStart != null && !isNaN(b.sourceStart)) ? b.sourceStart : (isIntro ? 0 : parseTime(seg?.start || '00:00:00'));
            const pipPath = b.overlaySource || seg?.overlay?.video;
            const hasPipWave = Boolean(!isIntro && b.hasOverlay && pipPath);

            const peaks = mainPath ? waveforms[mainPath] : undefined;
            const isCustom = Boolean(seg?.audio && seg.audio !== seg.video);
            const aName = seg?.audio ? (seg.audio.split('/').pop() || seg.audio) : '';
            const isSilent = Boolean(mainPath && peaks !== undefined && (!peaks.length || peaks.every(p => p < 0.001)));

            return (
              <div
                key={b.key} className={classes.timelineBlock} data-selected={isSelected || undefined} draggable={!isIntro}
                style={{ width: `${(b.duration / totalDuration) * 100}%`, backgroundColor: isIntro ? TL_INTRO : (isSelected ? TL_SELECTED : TL_OTHER), outline: (!isIntro && dragOverSegId === b.segId) ? '2px dashed #fff' : undefined, outlineOffset: '-3px' }}
                onMouseDown={e => { if (e.button === 0 && !isIntro) onSelectSegment(b.segId); }}
                onContextMenu={e => { if (isIntro) return; e.preventDefault(); onSelectSegment(b.segId); setMenuCtx({ segId: b.segId!, time: timeFromClientX(e.clientX) }); setMenuPos({ x: e.clientX, y: e.clientY }); }}
                onDragStart={isIntro ? undefined : e => { e.dataTransfer.setData('application/x-seg-id', b.segId!); e.dataTransfer.effectAllowed = 'move'; isDragging.current = false; }}
                onDragOver={e => { e.preventDefault(); if (!isIntro) setDragOverSegId(b.segId); }}
                onDragLeave={() => setDragOverSegId(prev => (prev === b.segId ? null : prev))}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation(); setDragOverSegId(null);
                  const fromId = e.dataTransfer.getData('application/x-seg-id');
                  if (fromId && !isIntro && b.segId && fromId !== b.segId) {
                    onReorder(fromId, b.segId);
                  } else if (!fromId) {
                    const p = e.dataTransfer.getData('text/plain');
                    if (p) onAppendFromBin(p);
                  }
                }}
                onDragEnd={() => { isDragging.current = false; setDragOverSegId(null); }}
              >
                {isVisible && mainPath && (
                  <div style={{ position: 'absolute', top: 0, left: `${leftPercent}%`, width: `${widthPercent}%`, height: hasPipWave ? '60%' : '100%', pointerEvents: 'none' }}>
                    <WaveformCanvas peaks={waveforms[mainPath]} startSec={resolvedStart + offsetSec} durationSec={visDuration} volume={isIntro ? 1 : (seg?.volume ?? 1)} />
                  </div>
                )}
                {isVisible && hasPipWave && (
                  <div style={{ position: 'absolute', bottom: 0, left: `${leftPercent}%`, width: `${widthPercent}%`, height: '40%', borderTop: '1px solid rgba(255, 121, 198, 0.4)', pointerEvents: 'none' }}>
                    <WaveformCanvas peaks={waveforms[pipPath!]} startSec={(b.overlaySourceStart ?? (seg?.overlay ? parseTime(seg.overlay.start) : 0)) + offsetSec} durationSec={visDuration} volume={2.2} color="rgba(255, 121, 198, 0.95)" />
                  </div>
                )}
                <span className={classes.blockLabel} style={{ zIndex: 2 }}>{b.label}</span>
                {b.hasOverlay && <span className={classes.pipBadge} style={{ zIndex: 3 }}>PiP</span>}
                {isCustom ? (
                  <span className={isSilent ? classes.silentBadge : classes.audioBadge} title={aName}>{isSilent ? `🔇 ${aName} (нет звука)` : `🎵 ${aName}`}</span>
                ) : (isSilent ? <span className={classes.silentBadge}>🔇 Без звука</span> : (peaks === undefined ? <span className={classes.loadingBadge}>⏳ Загрузка звука...</span> : null))}
              </div>
            );
          })}
        </div>
      </div>

      <Text ta="right" fz="xs" c="dimmed" mt="xs" style={{ userSelect: 'none' }}>Колёсико — зум · горизонтальный скролл — перемещение</Text>

      <Menu opened={menuCtx !== null} onChange={open => !open && setMenuCtx(null)} position="bottom-start" withinPortal>
        <Menu.Target><div style={{ position: 'fixed', left: menuPos.x, top: menuPos.y, width: 0, height: 0 }} /></Menu.Target>
        <Menu.Dropdown>
          {menuCtx && (() => {
            const seg = segments.find(s => s.id === menuCtx.segId);
            const b = displayBlocks.find(x => x.segId === menuCtx.segId);
            const isPlayheadHere = b && currentTime >= b.globalStart + 0.05 && currentTime <= b.globalStart + b.duration - 0.05;
            return (
              <>
                {isPlayheadHere && <Menu.Item leftSection={<Scissors size={14} />} onClick={() => { onSplit(menuCtx.segId, currentTime); setMenuCtx(null); }}>Разрезать по курсору ({formatTime(currentTime)})</Menu.Item>}
                <Menu.Item leftSection={<Scissors size={14} />} onClick={() => { onSplit(menuCtx.segId, menuCtx.time); setMenuCtx(null); }}>Разрезать в месте клика ({formatTime(menuCtx.time)})</Menu.Item>
                <Menu.Item color="red" leftSection={<Trash size={14} />} onClick={() => { onRemove(menuCtx.segId); setMenuCtx(null); }}>Удалить фрагмент</Menu.Item>
                <Menu.Divider />
                <Menu.Item onClick={() => { onToggleFade(menuCtx.segId, 'fade_in'); setMenuCtx(null); }}>{seg?.fade_in ? 'Убрать фейд в начале' : 'Добавить фейд в начале'}</Menu.Item>
                <Menu.Item onClick={() => { onToggleFade(menuCtx.segId, 'fade_out'); setMenuCtx(null); }}>{seg?.fade_out ? 'Убрать фейд в конце' : 'Добавить фейд в конце'}</Menu.Item>
              </>
            );
          })()}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
